import { useTheme } from "@emotion/react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  Typography,
  Stack,
  Menu,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { tokens } from "../../theme";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SidebarTenant from "./SidebarTenant";
import LanguageSelector from "../../components/LanguageSelector";
import NotificationsIcon from "@mui/icons-material/Notifications";
import subscriptionService from "../../services/subscriptionService";

export default function TopbarOfTenant({
  getSnackBarS,
  menuOpen: controlledMenuOpen,
  setMenuOpen: controlledSetMenuOpen,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { t } = useTranslation('tenantDashboard');
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const loadNotifications = () => {
    const t = localStorage.getItem("tokenTenant");
    if (!t) return;
    subscriptionService
      .listTenantNotifications(t)
      .then((d) => setNotifications(d.notifications || []))
      .catch(() => {});
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const isControlled =
    typeof controlledMenuOpen === "boolean" &&
    typeof controlledSetMenuOpen === "function";

  const menuOpen = isControlled ? controlledMenuOpen : internalMenuOpen;
  const setMenuOpen = isControlled ? controlledSetMenuOpen : setInternalMenuOpen;

  const gradientBackground = useMemo(() => {
    const sidebarGradient = `linear-gradient(160deg, ${colors.primary[500]}f5 0%, ${colors.primary[600]}f0 60%, ${colors.primary[700]}ee 100%)`;
    return sidebarGradient;
  }, [colors]);

  const handleAddDoctor = () => {
    navigate("/tenant/invite-admin-doctor");
  };

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const handleOpenNotif = (e) => {
    setNotifAnchor(e.currentTarget);
    loadNotifications();
  };

  const handleNotifClick = async (row) => {
    const t = localStorage.getItem("tokenTenant");
    if (t && row.id) {
      try {
        await subscriptionService.markNotificationRead(row.id, t);
      } catch (_) {}
    }
    setNotifications((prev) =>
      prev.map((n) => (n.id === row.id ? { ...n, read_at: new Date().toISOString() } : n))
    );
    setNotifAnchor(null);
    if (row.type === "doctor_quota_blocked") {
      navigate("/tenant/subscription");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("tokenTenant");
    localStorage.removeItem("tenantId");
    localStorage.removeItem("tenantInfo");
    localStorage.removeItem("tenantRequiresCheckout");
    localStorage.removeItem("languageManuallySet"); // Manuel dil tercihini temizle
    navigate("/corporate-login");
  };

  return (
    <Box
      sx={{
        width: "100%",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        backdropFilter: "blur(12px)",
        background: gradientBackground,
        borderBottomLeftRadius: { xs: 0, md: 20 },
        borderBottomRightRadius: { xs: 0, md: 20 },
        boxShadow: `0 16px 36px ${colors.primary[900]}35`,
        borderBottom: `1px solid ${colors.blueAccent[600]}30`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, md: 4, lg: 5 },
          py: { xs: 1.5, md: 2.5 },
          gap: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={() => setMenuOpen((prev) => !prev)}
            sx={{
              display: { xs: "inline-flex", md: "none" },
              backgroundColor: `${colors.primary[500]}88`,
              borderRadius: 2,
              "&:hover": {
                backgroundColor: `${colors.primary[500]}bb`,
              },
              transition: "all 0.25s ease",
            }}
          >
            <MenuIcon
              sx={{
                fontSize: 26,
                color: colors.grey[100],
              }}
            />
          </IconButton>

          <Avatar
            variant="rounded"
            sx={{
              display: { xs: "none", md: "flex" },
              width: 52,
              height: 52,
              background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
              boxShadow: `0 12px 26px ${colors.blueAccent[900]}55`,
            }}
          >
            <ShieldOutlinedIcon sx={{ fontSize: 30, color: colors.grey[100] }} />
          </Avatar>

          <Box>
            <Typography
              variant="h4"
              sx={{
                color: colors.grey[100],
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {t('authorizedControlPanel')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[200],
                opacity: 0.8,
              }}
            >
              {t('manageDoctorsPermissions')}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton
            onClick={handleOpenNotif}
            sx={{ color: colors.grey[100] }}
            aria-label="Bildirimler"
          >
            <Badge badgeContent={unreadCount} color="warning">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Menu
            anchorEl={notifAnchor}
            open={Boolean(notifAnchor)}
            onClose={() => setNotifAnchor(null)}
            PaperProps={{ sx: { maxWidth: 360, maxHeight: 400 } }}
          >
            {notifications.length === 0 && (
              <MenuItem disabled>Bildirim yok</MenuItem>
            )}
            {notifications.map((n) => (
              <MenuItem
                key={n.id}
                onClick={() => handleNotifClick(n)}
                sx={{
                  whiteSpace: "normal",
                  alignItems: "flex-start",
                  opacity: n.read_at ? 0.65 : 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {n.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {n.message}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>

          <Button
            onClick={handleAddDoctor}
            variant="contained"
            startIcon={<PersonAddAlt1Icon sx={{ fontSize: 20 }} />}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              textTransform: "none",
              fontWeight: 700,
              letterSpacing: 0.4,
              borderRadius: 3,
              px: 3,
              py: 1.2,
              background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
              color: colors.grey[100],
              boxShadow: `0 12px 30px ${colors.greenAccent[700]}50`,
              "&:hover": {
                transform: "translateY(-3px) scale(1.02)",
                boxShadow: `0 16px 34px ${colors.greenAccent[700]}65`,
                background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
              },
              transition: "all 0.25s ease",
            }}
          >
            {t('addDoctor')}
          </Button>

          <Button
            onClick={handleLogout}
            variant="contained"
            startIcon={<LogoutIcon sx={{ fontSize: 20 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              letterSpacing: 0.4,
              borderRadius: 3,
              px: { xs: 2.4, sm: 3 },
              py: 1.2,
              background: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.redAccent[500]} 100%)`,
              color: colors.grey[100],
              boxShadow: `0 12px 30px ${colors.redAccent[900]}45`,
              "&:hover": {
                transform: "translateY(-3px) scale(1.02)",
                boxShadow: `0 18px 38px ${colors.redAccent[900]}60`,
                background: `linear-gradient(135deg, ${colors.redAccent[700]} 0%, ${colors.redAccent[500]} 100%)`,
              },
              transition: "all 0.25s ease",
            }}
          >
            {t('logOut')}
          </Button>

          <IconButton
            onClick={() => setMenuOpen((prev) => !prev)}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              borderRadius: 3,
              border: `1px solid ${colors.blueAccent[500]}55`,
              backgroundColor: `${colors.primary[500]}55`,
              "&:hover": {
                backgroundColor: `${colors.primary[500]}80`,
                transform: "translateY(-2px)",
              },
              transition: "all 0.25s ease",
            }}
          >
            <MenuIcon sx={{ fontSize: 24, color: colors.grey[100] }} />
          </IconButton>

          <LanguageSelector />
        </Stack>
      </Box>

      {!isControlled && (
        <SidebarTenant menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      )}
    </Box>
  );
}
